import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import Papa from 'papaparse';
import {
  Table,
  Button,
  Space,
  Typography,
  Form,
  message,
  Tag,
  Popconfirm,
  Pagination,
  Tabs,
} from 'antd';
import {
  GiftOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import CouponFormModal from './components/CouponFormModal';
import CouponImportModal from './components/CouponImportModal';
import './ExtraCoupon.css';

dayjs.extend(buddhistEra);
dayjs.locale('th');

const { Title, Text } = Typography;

const COUPON_TYPE_CONFIG = {
  product: { label: 'ลดสินค้า', color: 'green' },
  shipping: { label: 'ลดค่าขนส่ง', color: 'blue' },
  free_shipping: { label: 'ฟรีค่าจัดส่ง', color: 'purple' },
  cashback: { label: 'เงินคืนลดรอบหน้า', color: 'orange' },
};

const DEFAULT_COUPON = {
  coupon_id: '',
  min_order: 1,
  discount: 1,
  cashback_percent: 3.25,
  max_discount: undefined,
  expiredate: dayjs(),
  coupon_type: 'product',
  usage_limit: 1, // 1 = single use, -1 = unlimited
  productcategory_id: [],
  per_user_limit: 1,
  is_public_display: true,
  is_stackable: true,
  allowed_payment_methods: [],
};

function ExtraCoupon(props) {
  const [form] = Form.useForm();
  const inputBulk = useRef(null);
  const [filedata, setFileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalImportOpen, setIsModalImportOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [coupon_list, setCoupon_list] = useState([]);
  const [category_list, setCategory_list] = useState([]);
  const [couponmode, setCouponmode] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const apiPost = (endpoint, formData) =>
    axios.post(props.app_hostname + endpoint, formData, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${props.permitdata?.token || ''}`,
      },
    });

  useEffect(() => {
    getCouponList();
    getCategoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getCategoryList() {
    const formData = new FormData();
    formData.append('filter', 'approved');
    axios
      .post(props.app_hostname + '/api/product/category/getlist', formData, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${props.permitdata?.token || ''}`,
        },
      })
      .then((response) => {
        if (response.data.status === 'OK') {
          setCategory_list(response.data.products || []);
        }
      })
      .catch(() => {});
  }

  async function getCouponList() {
    setLoading(true);
    const formData = new FormData();
    formData.append('filter', 'approved');

    apiPost('/api/order/coupon/getlist', formData)
      .then((response) => {
        setLoading(false);
        if (response.data.status === 'OK') {
          setCoupon_list(response.data.coupon || []);
        } else {
          message.error(response.data.message || 'เกิดข้อผิดพลาด');
        }
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      });
  }

  const isCouponActive = (record) => {
    if (record.isexpire) return false;
    if (record.coupon_status === 'Used') return false;
    if (record.usage_limit > 0 && (record.usage_count || 0) >= record.usage_limit) return false;
    return true;
  };

  const filteredCouponList = useMemo(() => {
    if (activeTab === 'active') {
      return coupon_list.filter(isCouponActive);
    }
    if (activeTab === 'expired') {
      return coupon_list.filter((c) => !isCouponActive(c));
    }
    return coupon_list;
  }, [coupon_list, activeTab]);

  const activeCount = useMemo(() => coupon_list.filter(isCouponActive).length, [coupon_list]);
  const expiredCount = useMemo(
    () => coupon_list.filter((c) => !isCouponActive(c)).length,
    [coupon_list],
  );

  const handleRandomId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = 'MEYA';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setFieldsValue({ coupon_id: result });
  };

  function openModal(mode, record = null) {
    setCouponmode(mode);

    let values;
    if (record) {
      // Edit mode: -1 = unlimited, 1 = single use, >1 = custom
      const limit = record.usage_limit ?? 1;
      const isUnlimited = limit === -1;
      const isCustom = limit > 1;
      values = {
        coupon_id: record.coupon_id,
        min_order: record.min_order,
        discount: record.discount,
        expiredate:
          record.expiredate && dayjs(record.expiredate).isValid()
            ? dayjs(record.expiredate)
            : dayjs(),
        coupon_type: record.coupon_type || 'product',
        usage_limit: isUnlimited ? -1 : isCustom ? 0 : limit, // 0 = custom radio value
        custom_usage_limit: isCustom ? limit : undefined,
        productcategory_id: record.productcategory_id
          ? record.productcategory_id
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        per_user_limit: record.per_user_limit ?? 1,
        is_public_display: record.is_public_display !== false,
        cashback_percent: record.cashback_percent ?? 3.25,
        max_discount: record.max_discount || undefined,
        is_stackable: record.is_stackable !== false,
        allowed_payment_methods: record.allowed_payment_methods
          ? record.allowed_payment_methods
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };
    } else {
      values = DEFAULT_COUPON;
    }
    form.setFieldsValue(values);
    setIsModalOpen(true);
  }

  const handleAdd = () => openModal('add');
  const handleEdit = (record) => openModal('edit', record);

  async function handleSubmit(values) {
    setModalLoading(true);

    const formData = new FormData();
    formData.append('coupon_id', values.coupon_id);
    formData.append('min_order', values.min_order);
    const finalDiscount =
      values.coupon_type === 'free_shipping' || values.coupon_type === 'cashback'
        ? 0
        : values.discount || 0;
    formData.append('discount', finalDiscount);
    formData.append(
      'cashback_percent',
      values.coupon_type === 'cashback' ? values.cashback_percent || 0 : 0,
    );
    formData.append('max_discount', values.max_discount || 0);
    formData.append('expiredate', dayjs(values.expiredate).format('YYYY-MM-DD'));
    formData.append('username', props.permitdata?.username || '');
    formData.append('coupon_type', values.coupon_type || 'product');
    // usage_limit: -1 = unlimited, 1 = single use, 0 = custom (use custom_usage_limit)
    const finalUsageLimit =
      values.usage_limit === 0 ? (values.custom_usage_limit ?? 1) : (values.usage_limit ?? 1);
    formData.append('usage_limit', finalUsageLimit);
    const catString = Array.isArray(values.productcategory_id)
      ? values.productcategory_id.filter(Boolean).join(',')
      : values.productcategory_id || '';
    formData.append('productcategory_id', catString);
    formData.append('per_user_limit', values.per_user_limit ?? 1);
    formData.append('is_public_display', values.is_public_display !== false ? '1' : '0');
    formData.append('is_stackable', values.is_stackable !== false ? '1' : '0');
    const paymentMethodStr = Array.isArray(values.allowed_payment_methods)
      ? values.allowed_payment_methods.filter(Boolean).join(',')
      : values.allowed_payment_methods || '';
    formData.append('allowed_payment_methods', paymentMethodStr);

    apiPost(`/api/order/coupon/${couponmode}`, formData)
      .then((response) => {
        setModalLoading(false);
        if (response.data.status === 'OK') {
          setCoupon_list(response.data.coupon || []);
          setIsModalOpen(false);
          message.success('บันทึก Coupon สำเร็จ');
        } else {
          message.error(response.data.message || 'เกิดข้อผิดพลาด');
        }
      })
      .catch((err) => {
        setModalLoading(false);
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      });
  }

  async function handleDelete(record) {
    setLoading(true);

    const formData = new FormData();
    formData.append('coupon_id', record.coupon_id);

    apiPost('/api/order/coupon/delete', formData)
      .then((response) => {
        setLoading(false);
        if (response.data.status === 'OK') {
          setCoupon_list((prevList) =>
            prevList.filter((item) => item.coupon_id !== record.coupon_id),
          );
          message.success('ลบ Coupon สำเร็จ');
        } else {
          message.error(response.data.message || 'เกิดข้อผิดพลาด');
        }
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      });
  }

  async function handleBulkImport() {
    setModalLoading(true);

    const formData = new FormData();
    formData.append('coupon_data', JSON.stringify(filedata));
    formData.append('username', props.permitdata?.username || '');

    apiPost('/api/order/coupon/import', formData)
      .then((response) => {
        setModalLoading(false);
        if (response.data.status === 'OK') {
          setCoupon_list(response.data.coupon || []);
          message.success('นำเข้า Coupon สำเร็จ');
          setFileData([]);
          setIsModalImportOpen(false);
        } else {
          message.error(response.data.message || 'เกิดข้อผิดพลาด');
        }
      })
      .catch((err) => {
        setModalLoading(false);
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      });
  }

  function clickBulk() {
    const inp = inputBulk.current;
    if (inp) {
      inp.click();
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length) {
      const inputFile = e.target.files[0];

      if (inputFile) {
        Papa.parse(inputFile, {
          complete: (result) => {
            // Step 1: Validate fields considering free_shipping type
            const validItems = result.data.filter((item) => {
              const isValidId = item.coupon_id && String(item.coupon_id).trim().length === 9;
              const minOrder = Number(item.min_order);
              const isValidMinOrder = !isNaN(minOrder) && minOrder >= 0;

              if (item.coupon_type === 'free_shipping') {
                return isValidId && isValidMinOrder;
              }
              const discount = Number(item.discount);
              return isValidId && isValidMinOrder && !isNaN(discount) && discount > 0;
            });

            // Step 2: Unique filtering O(N) using Set
            const seen = new Set();
            const unique = validItems.filter((item) => {
              const duplicate = seen.has(item.coupon_id);
              seen.add(item.coupon_id);
              return !duplicate;
            });

            setFileData(unique);
            setIsModalImportOpen(true);
          },
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
        });
      }
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      align: 'center',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'คูปอง',
      key: 'coupon_info',
      width: 220,
      render: (_, record) => {
        const typeConfig = COUPON_TYPE_CONFIG[record.coupon_type] || COUPON_TYPE_CONFIG.product;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Text strong style={{ fontSize: '14px', letterSpacing: '0.5px', color: '#1a1a1a' }}>
                {record.coupon_id}
              </Text>
            </div>
            <Space size={[0, 4]} wrap>
              <Tag color={typeConfig.color} style={{ margin: 0, fontSize: '11px' }}>
                {typeConfig.label}
              </Tag>
              {record.is_public_display !== false ? (
                <Tag color='cyan' style={{ margin: 0, fontSize: '11px' }}>
                  🌐 แสดงให้ลูกค้า
                </Tag>
              ) : (
                <Tag color='default' style={{ margin: 0, fontSize: '11px' }}>
                  🔒 โค้ดลับ
                </Tag>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: 'มูลค่าส่วนลด & ขั้นต่ำ',
      key: 'discount_condition',
      width: 170,
      sorter: (a, b) => Number(a.discount) - Number(b.discount),
      render: (_, record) => {
        const isFreeShip = record.coupon_type === 'free_shipping';
        const isCashback = record.coupon_type === 'cashback';
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: '14px',
                color: isFreeShip ? '#722ed1' : isCashback ? '#fa8c16' : '#52c41a',
              }}
            >
              {isFreeShip
                ? 'ฟรีค่าจัดส่ง'
                : isCashback
                  ? `คืน ${record.cashback_percent}%`
                  : `฿${Number(record.discount).toLocaleString()}`}
            </span>
            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
              ขั้นต่ำ ฿{Number(record.min_order).toLocaleString()}
              {isCashback && record.max_discount
                ? ` (สูงสุด ฿${Number(record.max_discount).toLocaleString()})`
                : ''}
            </span>
          </div>
        );
      },
    },
    {
      title: 'เงื่อนไข & สิทธิ์',
      key: 'scope_limits',
      width: 220,
      render: (_, record) => {
        const limit = record.usage_limit ?? 1;
        const count = record.usage_count ?? 0;
        const usageText = limit === -1 ? `${count} / ∞` : `${count} / ${limit}`;
        const catIds = record.productcategory_id;
        const ids = catIds
          ? catIds
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];

        const pms = record.allowed_payment_methods
          ? record.allowed_payment_methods
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        const pmLabels = {
          creditcard: '💳 บัตร',
          promtpay: '📱 พร้อมเพย์',
          cash: '💵 เงินสด',
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px' }}>
            <div>
              <span style={{ color: '#8c8c8c' }}>ใช้แล้ว: </span>
              <strong style={{ color: '#1677ff' }}>{usageText}</strong>
              <span style={{ margin: '0 4px', color: '#d9d9d9' }}>•</span>
              {record.per_user_limit === -1 ? (
                <Tag color='green' style={{ margin: 0, fontSize: '10px', padding: '0 4px' }}>
                  ไม่จำกัด
                </Tag>
              ) : (
                <Tag color='blue' style={{ margin: 0, fontSize: '10px', padding: '0 4px' }}>
                  {record.per_user_limit ?? 1} สิทธิ์/คน
                </Tag>
              )}
            </div>
            <div>
              <span style={{ color: '#8c8c8c' }}>หมวดหมู่: </span>
              {ids.length === 0 ? (
                <Tag color='default' style={{ margin: 0, fontSize: '10px' }}>
                  ทุกหมวดหมู่
                </Tag>
              ) : (
                <Space size={[0, 2]} wrap>
                  {ids.map((id) => {
                    const cat = category_list.find(
                      (c) => (c.productcategory_id || '').trim() === id.trim(),
                    );
                    return (
                      <Tag color='cyan' key={id} style={{ margin: 0, fontSize: '10px' }}>
                        {cat ? cat.productcategory_name : id}
                      </Tag>
                    );
                  })}
                </Space>
              )}
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
              <Tag
                color={record.is_stackable !== false ? 'geekblue' : 'purple'}
                style={{ margin: 0, fontSize: '10px' }}
              >
                {record.is_stackable !== false ? '🔗 ใช้ร่วมได้' : '🔒 ใช้เดี่ยว'}
              </Tag>
              {pms.length > 0 &&
                pms.map((pm) => (
                  <Tag color='orange' key={pm} style={{ margin: 0, fontSize: '10px' }}>
                    {pmLabels[pm] || pm}
                  </Tag>
                ))}
            </div>
          </div>
        );
      },
    },
    {
      title: 'สถานะ & วันหมดอายุ',
      key: 'status_expiry',
      width: 170,
      sorter: (a, b) => dayjs(a.expiredate).unix() - dayjs(b.expiredate).unix(),
      render: (_, record) => {
        let statusTag = <Tag color='success'>พร้อมใช้งาน</Tag>;
        if (record.isexpire) {
          statusTag = <Tag color='error'>หมดอายุ</Tag>;
        } else if (record.coupon_status !== 'Free') {
          statusTag = <Tag color='default'>{record.coupon_status}</Tag>;
        }

        const expDate =
          record.expiredate && dayjs(record.expiredate).isValid()
            ? dayjs(record.expiredate).format('DD MMM YYYY')
            : '-';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div>{statusTag}</div>
            <span style={{ fontSize: '12px', color: '#595959' }}>
              <ClockCircleOutlined
                style={{ marginRight: '4px', fontSize: '11px', color: '#8c8c8c' }}
              />
              {expDate}
            </span>
          </div>
        );
      },
    },
    {
      title: 'จัดการ',
      key: 'action',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Space>
          {!record.isexpire && record.coupon_status === 'Free' && (
            <Button
              type='primary'
              icon={<EditOutlined />}
              size='small'
              onClick={() => handleEdit(record)}
            >
              แก้ไข
            </Button>
          )}
          <Popconfirm
            title='ยืนยันการลบ'
            description='คุณแน่ใจหรือไม่ที่จะลบ Coupon นี้?'
            onConfirm={() => handleDelete(record)}
            okText='ใช่'
            cancelText='ไม่'
          >
            <Button type='primary' danger icon={<DeleteOutlined />} size='small'>
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const importColumns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Coupon ID',
      dataIndex: 'coupon_id',
      key: 'coupon_id',
    },
    {
      title: 'Min. Order',
      dataIndex: 'min_order',
      key: 'min_order',
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
    },
    {
      title: 'Expire Date',
      dataIndex: 'expiredate',
      key: 'expiredate',
    },
    {
      title: 'Type',
      dataIndex: 'coupon_type',
      key: 'coupon_type',
      render: (text) => {
        if (text === 'free_shipping') {
          return <Tag color='purple'>ฟรีค่าส่ง</Tag>;
        }
        if (text === 'shipping') {
          return <Tag color='blue'>ขนส่ง</Tag>;
        }
        if (text === 'cashback') {
          return <Tag color='orange'>เงินคืน</Tag>;
        }
        return <Tag color='green'>สินค้า</Tag>;
      },
    },
    {
      title: 'Limit',
      dataIndex: 'usage_limit',
      key: 'usage_limit',
      render: (text) => {
        const limit = text ?? 1;
        if (limit === -1) return '∞';
        return limit;
      },
    },
  ];

  const renderMobileCards = () => {
    return (
      <div className='coupon-mobile-view'>
        <div className='coupon-card-container'>
          {filteredCouponList
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((record) => {
              const limit = record.usage_limit ?? 1;
              const count = record.usage_count ?? 0;
              const usageText = limit === -1 ? `${count} / ∞` : `${count} / ${limit}`;

              return (
                <div className='coupon-card' key={record.coupon_id}>
                  <div className='coupon-card-header'>
                    <span className='coupon-id'>รหัส {record.coupon_id}</span>
                    {record.isexpire ? (
                      <Tag color='error' className='status-tag'>
                        หมดอายุ
                      </Tag>
                    ) : record.coupon_status === 'Free' ? (
                      <Tag color='success' className='status-tag'>
                        ใช้งานได้
                      </Tag>
                    ) : (
                      <Tag className='status-tag'>{record.coupon_status}</Tag>
                    )}
                  </div>
                  <div className='coupon-card-body'>
                    <div className='coupon-info-row'>
                      <span>
                        ส่วนลด{' '}
                        {record.coupon_type === 'free_shipping'
                          ? 'ฟรีค่าส่ง'
                          : record.coupon_type === 'cashback'
                            ? `คืน ${record.cashback_percent}%`
                            : record.discount}
                      </span>
                      <span>
                        ประเภท {COUPON_TYPE_CONFIG[record.coupon_type]?.label || 'ลดราคา'}
                      </span>
                    </div>
                    <div className='coupon-info-row' style={{ marginTop: '4px' }}>
                      <span>
                        ใช้แล้ว: <strong style={{ color: '#1890ff' }}>{usageText}</strong> ครั้ง
                      </span>
                    </div>
                    <div className='coupon-expiry' style={{ marginTop: '6px' }}>
                      วันหมดอายุ {dayjs(record.expiredate).format('YYYY-MMM-DD')}
                    </div>
                  </div>
                  <div className='coupon-card-actions'>
                    {!record.isexpire && record.coupon_status === 'Free' && (
                      <Button
                        className='btn-soft-blue'
                        size='small'
                        onClick={() => handleEdit(record)}
                      >
                        แก้ไข
                      </Button>
                    )}
                    <Popconfirm
                      title='ยืนยันการลบ'
                      description='คุณแน่ใจหรือไม่ที่จะลบ Coupon นี้?'
                      onConfirm={() => handleDelete(record)}
                      okText='ใช่'
                      cancelText='ไม่'
                    >
                      <Button className='btn-soft-red' size='small'>
                        ลบ
                      </Button>
                    </Popconfirm>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <div className='page-wrapper'>
      <div className='page-card'>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          <div className='page-header-actions'>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <GiftOutlined style={{ color: '#fa8c16', marginRight: '8px' }} />
                คูปอง
              </Title>
              <Text type='secondary'>จัดการคูปอง</Text>
            </div>
            <Space>
              <Button icon={<PlusOutlined />} type='primary' onClick={handleAdd}>
                เพิ่มใหม่
              </Button>
              <Button icon={<UploadOutlined />} onClick={clickBulk}>
                นำเข้าจำนวนมาก
                <input
                  hidden
                  type='file'
                  accept='.csv'
                  value=''
                  ref={inputBulk}
                  onChange={handleFileChange}
                />
              </Button>
              <Button icon={<ReloadOutlined />} onClick={getCouponList} loading={loading}>
                รีเฟรช
              </Button>
            </Space>
          </div>

          <div>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setCurrentPage(1);
              }}
              items={[
                {
                  key: 'active',
                  label: (
                    <span>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} /> ใช้งานได้ ({activeCount})
                    </span>
                  ),
                },
                {
                  key: 'expired',
                  label: (
                    <span>
                      <ClockCircleOutlined style={{ color: '#ff4d4f' }} /> หมดอายุ / ใช้ครบแล้ว (
                      {expiredCount})
                    </span>
                  ),
                },
                {
                  key: 'all',
                  label: (
                    <span>
                      <AppstoreOutlined /> ทั้งหมด ({coupon_list.length})
                    </span>
                  ),
                },
              ]}
              style={{ marginBottom: '16px' }}
            />

            <div className='coupon-desktop-view'>
              <Table
                columns={columns}
                dataSource={filteredCouponList}
                rowKey='coupon_id'
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: filteredCouponList.length,
                  showTotal: (total) => `รวม ${total} รายการ`,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onChange: (page, newPageSize) => {
                    setCurrentPage(page);
                    setPageSize(newPageSize);
                  },
                }}
                size='small'
                scroll={{ x: true }}
              />
            </div>
            {renderMobileCards()}
            <div className='coupon-mobile-view' style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredCouponList.length}
                  onChange={(page) => setCurrentPage(page)}
                  showSizeChanger={false}
                  size='small'
                />
              </div>
            </div>
          </div>
        </Space>
      </div>

      <CouponFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        loading={modalLoading}
        couponmode={couponmode}
        categoryList={category_list}
        form={form}
        handleRandomId={handleRandomId}
        couponTypeConfig={COUPON_TYPE_CONFIG}
      />

      <CouponImportModal
        isOpen={isModalImportOpen}
        onClose={() => {
          setIsModalImportOpen(false);
          setFileData([]);
        }}
        onImport={handleBulkImport}
        loading={modalLoading}
        fileData={filedata}
        importColumns={importColumns}
      />
    </div>
  );
}

ExtraCoupon.propTypes = {
  app_hostname: PropTypes.string,
  permitdata: PropTypes.shape({
    token: PropTypes.string,
    username: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
};

export default ExtraCoupon;
